import { useEffect, useState } from 'react';
import { Button, Checkbox, Group, Modal, Stack, Text, Textarea } from '@mantine/core';
import { IconCheck, IconTicket } from '@tabler/icons-react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { notifications } from '@mantine/notifications';
import { useNavigate } from 'react-router-dom';
import { api } from '../../lib/api';
import type { Todo } from '../../lib/api';
import { useActiveHub } from '../../hooks/useActiveHub';
import { TODO_QUERY_KEYS, customerLinkOf, ticketLinkOf } from './todoLabels';

/** Übergabe an die Ticket-Anlage (WeclappNewTicketPage liest das aus `location.state`). */
export interface FollowUpTicketState {
  fromTodo: { id: string; title: string; note: string | null };
  prefill: { subject: string; description: string };
}

interface Props {
  todo: Todo | null;
  onClose: () => void;
  onDone?: () => void;
  zIndex?: number;
}

/**
 * Abhaken einer Aufgabe: optionale Notiz („Kunde erreicht, will Angebot") — sie steht in der
 * Erledigt-Meldung an die Leitung. Mit „Folgeticket anlegen" geht es erst in die normale
 * Ticket-Anlage; erledigt ist die Aufgabe, sobald das Ticket dort angelegt ist. Nie automatisch:
 * weclapp verschickt die Erstellungs-Mail schon beim Anlegen.
 */
export function CompleteTodoModal({ todo, onClose, onDone, zIndex = 300 }: Props) {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { provider } = useActiveHub();
  const [note, setNote] = useState('');
  const [followUp, setFollowUp] = useState(false);

  useEffect(() => {
    setNote('');
    setFollowUp(false);
  }, [todo?.id]);

  const complete = useMutation({
    mutationFn: () => api.completeTodo(todo!.id, { note: note.trim() || null }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: TODO_QUERY_KEYS.all });
      void queryClient.invalidateQueries({ queryKey: TODO_QUERY_KEYS.ticketCockpits });
      notifications.show({
        title: 'Erledigt',
        message: todo?.kind === 'ASSIGNED' ? 'Die Leitung bekommt Bescheid.' : '',
        color: 'green',
      });
      onDone?.();
      onClose();
    },
    onError: (e: Error) => notifications.show({ title: 'Nicht erledigt', message: e.message, color: 'red' }),
  });

  const startFollowUp = () => {
    if (!todo) return;
    const ticket = ticketLinkOf(todo);
    const customer = customerLinkOf(todo);
    const params = new URLSearchParams();
    if (customer) params.set('customerId', customer.refId);
    else if (ticket?.meta?.partyId) {
      params.set('weclappCustomerId', String(ticket.meta.partyId));
      if (ticket.meta.partyName) params.set('weclappCustomerName', String(ticket.meta.partyName));
    }
    const trimmed = note.trim();
    const state: FollowUpTicketState = {
      fromTodo: { id: todo.id, title: todo.title, note: trimmed || null },
      prefill: {
        subject: (trimmed || todo.title).split('\n')[0].slice(0, 120),
        description: [
          trimmed || null,
          `Folgeticket zur Aufgabe „${todo.title}"`,
          ticket ? `Bezug: Ticket ${ticket.refNumber ?? ticket.refId}${ticket.label ? ` — ${ticket.label}` : ''}` : null,
        ]
          .filter(Boolean)
          .join('\n\n'),
      },
    };
    onClose();
    navigate(`/tickets/new${params.toString() ? `?${params}` : ''}`, { state });
  };

  const canFollowUp = provider === 'weclapp';

  return (
    <Modal opened={!!todo} onClose={onClose} title="Erledigt" centered zIndex={zIndex}>
      {todo && (
        <Stack gap="sm">
          <Text size="sm" fw={500}>
            {todo.title}
          </Text>
          <Textarea
            label="Notiz (optional)"
            description={todo.kind === 'ASSIGNED' ? 'Steht in der Erledigt-Meldung an die Leitung' : undefined}
            placeholder="z. B. Kunde erreicht, will Angebot"
            value={note}
            onChange={(e) => setNote(e.currentTarget.value)}
            autosize
            minRows={2}
            maxRows={6}
            maxLength={2000}
            data-autofocus
          />
          {canFollowUp && (
            <Checkbox
              label="Folgeticket anlegen"
              description="Öffnet die Ticket-Anlage mit Kunde und Notiz. Erledigt ist die Aufgabe, sobald das Ticket angelegt ist."
              checked={followUp}
              onChange={(e) => {
                const __checked = e.currentTarget.checked;
                setFollowUp(__checked);
              }}
            />
          )}
          <Group justify="flex-end" mt="xs">
            <Button variant="default" onClick={onClose} disabled={complete.isPending}>
              Abbrechen
            </Button>
            {followUp ? (
              <Button leftSection={<IconTicket size={16} />} onClick={startFollowUp}>
                Weiter zum Ticket
              </Button>
            ) : (
              <Button color="green" leftSection={<IconCheck size={16} />} loading={complete.isPending} onClick={() => complete.mutate()}>
                Erledigt
              </Button>
            )}
          </Group>
        </Stack>
      )}
    </Modal>
  );
}
